import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { ReactionType } from '@/components/ReactionPicker';
import { sendLikeNotification } from '@/services/triggerNotification';

interface PostReactions {
  total_count: number;
  user_reaction: ReactionType | null;
  reaction_counts: Array<{
    reaction_type: ReactionType;
    count: number;
  }>;
}

// Fetch post reactions data
export const usePostReactions = (postId: string) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['post-reactions', postId],
    queryFn: async (): Promise<PostReactions> => {
      // Get reaction counts
      const { data: reactions, error: reactionsError } = await supabase
        .from('reactions')
        .select('reaction_type, user_id')
        .eq('post_id', postId);

      if (reactionsError) throw reactionsError;

      // Calculate counts and user's reaction
      const reactionCounts = reactions?.reduce((acc, reaction) => {
        const existing = acc.find(r => r.reaction_type === reaction.reaction_type);
        if (existing) {
          existing.count++;
        } else {
          acc.push({ reaction_type: reaction.reaction_type as ReactionType, count: 1 });
        }
        return acc;
      }, [] as Array<{ reaction_type: ReactionType; count: number }>) || [];

      const userReaction = user ? 
        reactions?.find(r => r.user_id === user.id)?.reaction_type as ReactionType || null : 
        null;

      const totalCount = reactions?.length || 0;

      return {
        total_count: totalCount,
        user_reaction: userReaction,
        reaction_counts: reactionCounts.sort((a, b) => b.count - a.count)
      };
    },
    enabled: !!postId,
  });

  const addReaction = useMutation({
    mutationFn: async (reactionType: ReactionType) => {
      if (!user) throw new Error('Authentication required');

      // Get post owner to send notification
      const { data: post, error: postError } = await supabase
        .from('memory_posts')
        .select('user_id')
        .eq('id', postId)
        .single();

      const { error } = await supabase
        .from('reactions')
        .upsert({
          user_id: user.id,
          post_id: postId,
          reaction_type: reactionType
        }, {
          onConflict: 'user_id,post_id'
        });

      if (error) throw error;

      // Send notification to post owner (if not the same user)
      if (post && post.user_id !== user.id) {
        try {
          await sendLikeNotification(post.user_id, user.id, postId);
        } catch (notificationError) {
          console.error('Error sending reaction notification:', notificationError);
          // Don't throw - reaction was successful, notification failure shouldn't break the flow
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['post-reactions', postId] });
    },
  });

  const removeReaction = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error('Authentication required');

      const { error } = await supabase
        .from('reactions')
        .delete()
        .eq('user_id', user.id)
        .eq('post_id', postId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['post-reactions', postId] });
    },
  });

  return {
    data,
    isLoading,
    error,
    addReaction: addReaction.mutateAsync,
    removeReaction: removeReaction.mutateAsync,
    isAddingReaction: addReaction.isPending,
    isRemovingReaction: removeReaction.isPending,
  };
};